import { useCallback, useEffect, useMemo, useState } from 'react'
import { BellRing, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '../../contexts/AuthContext'
import { api, type NotificationPreferences } from '../../lib/api'
import { isNativeAndroid, isNativeIOS } from '../../mobile/capacitor'
import {
  getAppPushStatus,
  requestAndRegisterAppPush,
  type AppPushStatus,
} from '../../services/pushNotifications'

const PUSH_PROMPT_DISMISSED_PREFIX = 'optioneer_push_prompt_dismissed'

function dismissedKey(userId: string) {
  return `${PUSH_PROMPT_DISMISSED_PREFIX}:${userId}`
}

function shouldPromptForStatus(status: AppPushStatus) {
  return status === 'permission_required' || status === 'registration_required'
}

function pushPreferenceUpdateFrom(prefs: NotificationPreferences) {
  return {
    push_enabled: true,
    profit_target_push_enabled:
      prefs.profit_target_push_enabled || prefs.alert_profit_target || false,
    stop_loss_push_enabled: prefs.stop_loss_push_enabled || prefs.alert_stop_loss || false,
    expiry_warning_push_enabled:
      prefs.expiry_warning_push_enabled || prefs.alert_expiration_warning || false,
    auto_exercise_push_enabled:
      prefs.auto_exercise_push_enabled || prefs.auto_exercise_long_itm || false,
  }
}

export default function MobilePushPermissionPrompt() {
  const { user, loading } = useAuth()
  const [visible, setVisible] = useState(false)
  const [busy, setBusy] = useState(false)
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null)

  const userId = user?.id
  const storageKey = useMemo(() => (userId ? dismissedKey(userId) : null), [userId])

  useEffect(() => {
    if (loading || !userId || !storageKey || (!isNativeAndroid() && !isNativeIOS())) {
      setVisible(false)
      return
    }

    if (window.localStorage.getItem(storageKey) === 'true') {
      setVisible(false)
      return
    }

    let cancelled = false

    async function evaluatePrompt() {
      try {
        const [prefs, status] = await Promise.all([
          api.notificationPreferences.get(),
          getAppPushStatus(),
        ])

        if (cancelled) return

        setPreferences(prefs)
        setVisible(Boolean(prefs.app_alerts_entitled && shouldPromptForStatus(status)))
      } catch (error) {
        console.warn('[Push] Failed to evaluate native notification prompt:', error)
        if (!cancelled) setVisible(false)
      }
    }

    void evaluatePrompt()

    return () => {
      cancelled = true
    }
  }, [loading, storageKey, userId])

  const dismiss = useCallback(() => {
    if (storageKey) {
      window.localStorage.setItem(storageKey, 'true')
    }
    setVisible(false)
  }, [storageKey])

  const enableNotifications = useCallback(async () => {
    if (!preferences) return

    setBusy(true)
    try {
      const status = await requestAndRegisterAppPush()
      if (status === 'blocked') {
        toast.error(
          isNativeIOS()
            ? 'Notifications are blocked in iOS Settings.'
            : 'Notifications are blocked in Android settings.'
        )
        dismiss()
        return
      }
      if (status === 'registration_required') {
        toast.error('Push token registration did not complete. Try enabling again.')
        return
      }
      if (status === 'error') {
        toast.error('App notifications could not be enabled.')
        return
      }
      if (status !== 'enabled') {
        toast.error('Notification permission is required.')
        return
      }

      await api.notificationPreferences.update(pushPreferenceUpdateFrom(preferences))
      toast.success('App notifications enabled')
      setVisible(false)
    } catch (error) {
      console.error('[Push] Failed to enable app notifications:', error)
      toast.error('App notifications could not be enabled.')
    } finally {
      setBusy(false)
    }
  }, [dismiss, preferences])

  if (!visible) return null

  return (
    <div className="fixed inset-x-0 bottom-0 z-[90] px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:px-6">
      <div className="mx-auto max-w-md rounded-lg border border-slate-200 bg-white p-4 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">
            <BellRing className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-base font-semibold text-slate-950 dark:text-white">
                Enable app alerts
              </h2>
              <button
                type="button"
                onClick={dismiss}
                className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                aria-label="Dismiss app notification prompt"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              Get profit target, stop loss, and expiry alerts on this device.
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={dismiss}
                className="inline-flex justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={() => void enableNotifications()}
                disabled={busy}
                className="inline-flex justify-center rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-sky-500 dark:text-slate-950 dark:hover:bg-sky-400"
              >
                {busy ? 'Enabling...' : 'Enable notifications'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
